import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  useDeleteSelfExternalUserMutation,
  useGetExternalUserDetailQuery,
  useSaveExternalUserMutation,
} from '../../../../../redux/services/externalUsers.jsx';
import { Spinner } from '../../../../common/index.jsx';
import { AppBarCMS } from '../../../../basic/index.jsx';
import ExternalUserForm from '../../../../common/External/ExternalUserForm.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages';
import { useSelector, useDispatch } from 'react-redux';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import { ROLE_EXTERNAL_USERS_ADMIN } from '../../../../../utils/constants/roles.js';
import { logout } from '../../../../../redux/slices/authSlice.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';

const ExternalUsersEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: idFromUrl } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth.user);
  const isAdmin = useUserHasRole(ROLE_EXTERNAL_USERS_ADMIN);

  const {
    saveSuccess: toastSaveSuccess,
    successMessage,
  } = useToastMessages();

  // Determine which ID to load: admin can edit any user by URL param; otherwise load own profile
  const activeId = isAdmin ? idFromUrl : user?.id;

  const { data: externalUserData, isLoading: isLoadingExternalUserData } =
    useGetExternalUserDetailQuery(activeId, {
      skip: !activeId,
    });

  const [saveExternalUser] = useSaveExternalUserMutation();
  const [deleteSelfExternalUser] = useDeleteSelfExternalUserMutation();

  const initials = useMemo(
    () => ({
      ...externalUserData,
      title_before: externalUserData?.title_before,
      last_name: externalUserData?.last_name,
      first_name: externalUserData?.first_name,
      title_after: externalUserData?.title_after,
      email: externalUserData?.email,
      phone_number: externalUserData?.phone_number,
      date_of_birth: externalUserData?.date_of_birth
        ? dayjs(externalUserData.date_of_birth)
        : undefined,
      user_roles: externalUserData?.roles?.map((item) => item.id),
    }),
    [externalUserData],
  );

  const handleSaveExternalUser = async (values) => {
    try {
      await saveExternalUser({
        body: { id: values.id, ...values },
      }).unwrap();

      toastSaveSuccess();
      // If we are not on a CMS page, go back one page in history
      if (!String(location?.pathname || '').startsWith('/cms')) {
        navigate(-1);
      }
    } catch (_err) {
      // Global axios interceptor handles the error toast
    }
  };

  const handleDeleteExternalUser = async () => {
    try {
      await deleteSelfExternalUser().unwrap();
      dispatch(logout());
      successMessage({
        message: t('Používateľský účet bol vymazaný.'),
      });
      navigate(routes.homePage, { replace: true });
    } catch (_err) {
      // Global axios interceptor handles the error toast
    }
  };

  const pageTitle = idFromUrl
    ? t('Úprava externého používateľa')
    : !isAdmin
      ? t('Profil externého používateľa')
      : t('Vytvorenie nového externého používateľa');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  return isLoadingExternalUserData ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS title={pageTitle} />
      <div style={{ margin: '10px' }}>
        <ExternalUserForm
          initials={initials}
          onSubmit={handleSaveExternalUser}
          onDelete={!isAdmin ? handleDeleteExternalUser : undefined}
        />
      </div>
    </>
  );
};

export default ExternalUsersEdit;
